import classNames from "classnames";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";

import {
  BestSellingProductData,
  SalesDashboardData,
  SalesDashboardTagsData,
  SalesRecord,
} from "../../../data-store";
import useSalesDashboardData from "../../../data-store/useSalesDashboardData";
import useSalesDashboardSummaryData from "../../../data-store/useSalesDashboardSummaryData";
import useSalesDashboardTagsData from "../../../data-store/useSalesDashboardTagsData";
import { ImpactCategory } from "../../../domain/impactCategories";
import useImpactRatingInfos from "../../../services/useImpactRatingInfos";
import {
  ConvertableUnit,
  convertToTargetUnit,
  MassUnit,
  massUnits,
} from "../../../util/units";
import { ProportionsChartImpactRatingInfos } from "../../scope-3/ImpactRatingProportionsChart";
import StatusDisplay from "../../StatusDisplay";
import "../SalesDashboardPage.css";
import ImpactCategoryChart from "../ImpactCategoryChart";
import { useImpactCategory } from "../ImpactCategoryProvider";
import { ProductHighlights } from "../ProductHighlights";
import RatingDistributionBarChart from "../RatingDistributionBarChart";
import { RatingDistributionChart } from "../RatingDistributionChart";
import SalesAndAverageImpactByTags from "../SalesAndAverageImpactByTags";
import SalesImpactByTagsBarChart from "../SalesImpactByTagsBarChart";
import SalesSummaries from "../SalesSummaries";
import ChartsContainerController from "./ChartsContainerController";

type PeriodsOptions = "MONTHLY" | "QUARTERLY" | "YEARLY";

const INFO_MAP: Record<ImpactCategory, EffectType> = {
  GHG: "GHG_PER_KG",
  LAND_USE: "LAND_USE_PER_KG",
  WATER_USE: "WATER_USE_PER_KG",
};

const UNITS_CONVERSION: Record<
  ImpactCategory,
  "ghgEmissionsUnit" | "landUseUnit" | "waterUseUnit"
> = {
  GHG: "ghgEmissionsUnit",
  LAND_USE: "landUseUnit",
  WATER_USE: "waterUseUnit",
};

type EffectType =
  | "GHG_PER_KG"
  | "GHG_PER_SERVING"
  | "LAND_USE_PER_KG"
  | "LAND_USE_PER_SERVING"
  | "WATER_USE_PER_KG"
  | "WATER_USE_PER_SERVING";

interface ChartsContainerProps {}

const ChartsContainer: React.FC<
  PropsWithChildren<ChartsContainerProps>
> = () => {
  const intl = useIntl();

  const [salesDashboardDataStatus] = useSalesDashboardData();
  const [salesDashboardSummaryDataStatus] = useSalesDashboardSummaryData();
  const [salesDashboardTagsDataStatus] = useSalesDashboardTagsData();
  const { status: impactRatingInfosStatus } = useImpactRatingInfos();

  const availableCharts = ["IMPACT_CATEGORY", "RATING_DISTRIBUTION"];

  const availablePeriods: {
    label: string;
    value: PeriodsOptions;
  }[] = useMemo(() => {
    if (salesDashboardDataStatus.type !== "success") return [];
    return [
      ...(salesDashboardDataStatus?.value?.monthlyData
        ? [
            {
              label: intl.formatMessage({
                id: "components/pages:SalesDashBoard/monthly",
                defaultMessage: "Monthly",
              }),
              value: "MONTHLY" as PeriodsOptions,
            },
          ]
        : []),
      // Deprioritized for now
      ...(salesDashboardDataStatus.value?.quarterlyData
        ? [
            {
              label: intl.formatMessage({
                id: "components/pages:SalesDashBoard/quarterly",
                defaultMessage: "Quarterly",
              }),
              value: "QUARTERLY" as PeriodsOptions,
            },
          ]
        : []),
      // {
      //   label: intl.formatMessage({
      //     id: "components/pages:SalesDashBoard/yearly",
      //     defaultMessage: "Yearly",
      //   }),
      //   value: "YEARLY",
      // },
    ];
  }, [intl, salesDashboardDataStatus]);

  const [selectedPeriod, setSelectedPeriod] = useState<
    "MONTHLY" | "QUARTERLY" | "YEARLY"
  >(availablePeriods?.[0]?.value);
  const [selectedChart, setSelectedChart] = useState(availableCharts[0]);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  const { impactCategory } = useImpactCategory();

  const unit = UNITS_CONVERSION[impactCategory];
  useEffect(() => {
    if (availablePeriods.length) {
      setSelectedPeriod(availablePeriods?.[0]?.value);
    }
  }, [availablePeriods]);

  const handleChangeChart = useCallback((e) => {
    setSelectedChart(e);
  }, []);
  const handleChangePeriod = useCallback((e) => {
    setSelectedPeriod(e);
  }, []);
  const handleChangeYear = useCallback((e) => {
    setSelectedYear(e);
  }, []);

  const RENDERED_CHART = useCallback(
    (sales: SalesDashboardData) => {
      if (selectedYear == null || selectedChart == null) return;

      const convertGHG = (item: SalesRecord) => {
        return {
          ...item,
          ghgEmissions: convertToTargetUnit(
            item.ghgEmissions,
            item.ghgEmissionsUnit.find((i) =>
              massUnits.includes(i as MassUnit)
            ) as ConvertableUnit,
            "t"
          ),
        };
      };

      const filteredData = {
        monthlyData: sales?.monthlyData
          ?.filter(
            (item: any) =>
              item.salesDataTimePeriod.year === Number(selectedYear)
          )
          .map(convertGHG),
        quarterlyData: sales?.quarterlyData
          ?.filter(
            (item: any) =>
              item.salesDataTimePeriod.year === Number(selectedYear)
          )
          .map(convertGHG),
        yearlyData: sales?.yearlyData
          ?.filter(
            (item: any) =>
              item.salesDataTimePeriod.year === Number(selectedYear)
          )
          .map(convertGHG),
      };

      if (selectedChart === "IMPACT_CATEGORY") {
        return (
          <ImpactCategoryChart period={selectedPeriod} data={filteredData} />
        );
      }

      if (selectedChart === "RATING_DISTRIBUTION") {
        return (
          <RatingDistributionChart
            period={selectedPeriod}
            data={filteredData}
          />
        );
      }

      return (
        <ImpactCategoryChart period={selectedPeriod} data={filteredData} />
      );
    },
    [selectedYear, selectedChart, selectedPeriod]
  );

  const getAvailableYears = useCallback(
    (data: SalesDashboardData): string[] => {
      const allYears = new Set<number>();

      const extractYears = (records?: SalesRecord[]) => {
        records?.forEach((item) => {
          if (item.salesDataTimePeriod?.year) {
            allYears.add(item.salesDataTimePeriod.year);
          }
        });
      };

      extractYears(data?.monthlyData);
      extractYears(data?.quarterlyData);
      extractYears(data?.yearlyData);
      return Array.from(allYears)
        .sort((a, b) => b - a)
        .map(String);
    },
    []
  );

  const availableYears: string[] = useMemo(() => {
    if (
      salesDashboardDataStatus.type !== "success" ||
      salesDashboardDataStatus?.value == null
    )
      return [];
    return getAvailableYears(salesDashboardDataStatus?.value);
  }, [getAvailableYears, salesDashboardDataStatus]);

  useEffect(() => {
    setSelectedYear(availableYears[0]);
  }, [availableYears]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <div
        className={classNames("SalesDashboardCard", {
          SalesDashboardCard_loading:
            salesDashboardDataStatus.type === "loading",
        })}
      >
        <StatusDisplay.Many<[SalesDashboardData | null]>
          statuses={[salesDashboardDataStatus]}
        >
          {(sales) => {
            if (selectedYear == null || sales == null) return;
            return (
              <div>
                <ChartsContainerController
                  handleChangeChart={handleChangeChart}
                  handleChangePeriod={handleChangePeriod}
                  selectedChart={selectedChart}
                  availableCharts={availableCharts}
                  availablePeriods={availablePeriods}
                  selectedPeriod={selectedPeriod}
                  availableYears={availableYears}
                  selectedYear={selectedYear}
                  handleChangeYear={handleChangeYear}
                />
                {RENDERED_CHART(sales)}
              </div>
            );
          }}
        </StatusDisplay.Many>
      </div>
      <StatusDisplay.Many<[any, ProportionsChartImpactRatingInfos, any]>
        statuses={[
          salesDashboardSummaryDataStatus,
          impactRatingInfosStatus,
          salesDashboardDataStatus,
        ]}
      >
        {(summary, impactRatingInfos, ratingDist) => {
          const bestSellingERatedProduct = getBestSellingProduct(
            summary.bestSellingProductData,
            impactCategory,
            "high"
          );
          const bestSellingARatedProduct = getBestSellingProduct(
            summary.bestSellingProductData,
            impactCategory,
            "low"
          );
          return (
            <>
              <div
                className={classNames("SalesDashboardCard", {
                  SalesDashboardCard_loading:
                    salesDashboardDataStatus.type === "loading",
                })}
                style={{ padding: "16px 0px 20px 24px" }}
              >
                {selectedYear == null || summary == null ? null : (
                  <SalesSummaries
                    summary={summary}
                    impactRatingInfos={impactRatingInfos.impactRatingInfos}
                  />
                )}
              </div>
              <div className="SalesDashboard__RatingDistributionAndProductHighlights">
                <div
                  className={classNames("SalesDashboardCard", {
                    SalesDashboardCard_loading:
                      salesDashboardDataStatus.type === "loading",
                  })}
                >
                  <div>
                    <RatingDistributionBarChart data={ratingDist} />
                  </div>
                </div>
                <div
                  className={classNames("SalesDashboardCard p-0", {
                    SalesDashboardCard_loading:
                      salesDashboardDataStatus.type === "loading",
                  })}
                >
                  <ProductHighlights
                    highImpactProduct={bestSellingERatedProduct}
                    impactCategory={impactCategory}
                    lowImpactProduct={bestSellingARatedProduct}
                  />
                </div>
              </div>
            </>
          );
        }}
      </StatusDisplay.Many>
      <StatusDisplay.Many<
        [ProportionsChartImpactRatingInfos, SalesDashboardTagsData | null]
      >
        statuses={[impactRatingInfosStatus, salesDashboardTagsDataStatus]}
      >
        {({ impactRatingInfos }, tagsData) => {
          const effectType = INFO_MAP[impactCategory];
          const entry = impactRatingInfos.find(
            (r) => r.effectType === effectType
          );
          if (!tagsData) return null;
          return (
            <div className="SalesDashboard__SalesAndImpactByTagsContainer">
              <div
                className={classNames("SalesDashboardCard", {
                  SalesDashboardCard_loading:
                    salesDashboardDataStatus.type === "loading",
                })}
                style={{
                  padding: "16px 20px 16px 24px",
                  minHeight: "420px",
                  height: 420,
                }}
              >
                <SalesAndAverageImpactByTags
                  impactRatingInfo={entry}
                  tags={tagsData.collections}
                />
              </div>
              <div
                className={classNames("SalesDashboardCard", {
                  SalesDashboardCard_loading:
                    salesDashboardDataStatus.type === "loading",
                })}
                style={{
                  padding: "16px 20px 16px 24px",
                  minHeight: "420px",
                  height: 420,
                }}
              >
                <SalesImpactByTagsBarChart
                  tags={tagsData.collections}
                  unit={tagsData?.[unit]}
                />
              </div>
            </div>
          );
        }}
      </StatusDisplay.Many>
    </div>
  );
};

export default ChartsContainer;

function getBestSellingProduct(
  bestSellingProducts: BestSellingProductData,
  impactCategory: ImpactCategory,
  highOrLowImpact: "high" | "low"
) {
  switch (impactCategory) {
    case ImpactCategory.GHG:
      const productsGhg = bestSellingProducts.ghg;
      return highOrLowImpact === "high"
        ? productsGhg.bestSellingERatedProduct
        : productsGhg.bestSellingARatedProduct;
    case ImpactCategory.LAND_USE:
      const productsLandUse = bestSellingProducts.landUse;
      return highOrLowImpact === "high"
        ? productsLandUse.bestSellingERatedProduct
        : productsLandUse.bestSellingARatedProduct;
    case ImpactCategory.WATER_USE:
      const productsWaterUse = bestSellingProducts.waterUse;
      return highOrLowImpact === "high"
        ? productsWaterUse.bestSellingERatedProduct
        : productsWaterUse.bestSellingARatedProduct;
  }
}
